import { useState, useEffect } from "react";
import { Form, Input } from "antd";
import { backendGetRequest } from "../../../helpers/callBackend";
import CustomTagsHierarchy from "../transactions/customTagsHierarchy";
import Purposes from "./purposes";
import { documentTitle } from "../../../components/header/documentTitle";
import HeaderBox from "../../../components/generic/arpariBoxes/headerBox";

import { backendPostWithoutRefresh } from '../../../helpers/callBackend';
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import LoadingButton from '@mui/lab/LoadingButton';
import GenericModal from '../../../components/buttons/genericModal';
import OuterBox from "../../../components/generic/arpariBoxes/OuterBox";
import PermissionGate from '../../../helpers/PermissionGate'

const PlacementSettings = () => {

  // HIERARCHIES
  const [flattenedTags, setFlattenedTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupsPageCountExpected, setGroupsPageCountExpected] = useState(0);
  const [groupsPageCountReceived, setGroupsPageCountReceived] = useState(-1);
  const [flattenedTagsPageCountExpected, setFlattenedTagsPageCountExpected] = useState(0);
  const [flattenedTagsPageCountReceived, setFlattenedTagsPageCountReceived] = useState(-1);
  const [flattenedTagsLoading, setFlattenedTagsLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const BA_TAG = 1;


  // PURPOSES
  const [addPurposeSubmitting, setAddPurposeSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [addPurposeVisible, setAddPurposeVisible] = useState(false);
  const [savedPurposes, setSavedPurposes] = useState(
    JSON.parse(window.localStorage.getItem("bankAccountPurposes")) || []
  );


  useEffect(() => {
    documentTitle("Placement Settings");
  }, [])

  useEffect(() => {
    setFlattenedTagsLoading(flattenedTagsPageCountExpected > flattenedTagsPageCountReceived);
  }, [flattenedTagsPageCountExpected, flattenedTagsPageCountReceived]);

  useEffect(() => {
    setGroupsLoading(groupsPageCountExpected > groupsPageCountReceived);
  }, [groupsPageCountExpected, groupsPageCountReceived]);

  const addCustomTag = (newTag) => {
    setFlattenedTags(prevTags => [...prevTags, newTag]);
  };

  const deleteCustomTag = (deletedTagId) => {
    setFlattenedTags(prevTags => prevTags.filter(tag => tag?.id !== deletedTagId));
  };

  const editCustomTag = (editedTag) => {
    setFlattenedTags(prevTags =>
      prevTags.map(tag => (tag?.id === editedTag?.id ? editedTag : tag))
    );
  };

  const addGroup = (newGroup) => {
    if (newGroup && Object.keys(newGroup).length) {
      setGroups(prevGroups => [...prevGroups, newGroup]);
    }
  };

  const deleteGroup = (deletedGroupId) => {
    setGroups(prevGroups => prevGroups.filter(group => group?.id !== deletedGroupId));
  };

  const editGroup = (editedGroup) => {
    setGroups(prevGroups =>
      prevGroups.map(group => (group?.id === editedGroup?.id ? editedGroup : group))
    );
  };

  useEffect(() => {
    backendGetRequest("/api/generic-tag/group/", { params: { page_index: 0, group_type: BA_TAG } })
      .then((res) => {
        setGroupsPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequest("api/generic-tag/group/", { params: { page_index: i, group_type: BA_TAG } })
            .then((res) => {
              const generic_groups = res?.data?.generic_groups;
              setGroups(prevState => [...prevState, ...generic_groups]);
              setGroupsPageCountReceived(prevState => prevState + 1);
            })
            .catch(() => {
              setGroupsPageCountExpected(prevState => prevState - 1);
            })
        }
      });
  }, []);


  const addPurpose = () => {
    form.validateFields().then(values => {
      if (!values.purposeName.trim()) {
        return;
      }

      setAddPurposeSubmitting(true);
      backendPostWithoutRefresh("/api/bank-account/purpose/", {
        purpose: values.purposeName,
        description: values.purposeDescription || ""
      })
        .then((res) => {
          setAddPurposeSubmitting(false);
          setSavedPurposes(prev => [...prev, res.data.purpose]);
          form.resetFields();
          setAddPurposeVisible(false);
        })
        .catch(() => {
          setAddPurposeSubmitting(false);
        });
    });
  };

  useEffect(() => {
    backendGetRequest("/api/generic-tag/", { params: { page_index: 0, group_type: BA_TAG } })
      .then((res) => {
        setFlattenedTagsPageCountExpected(res?.data?.total_pages);
        for (let i = 1; i <= res?.data?.total_pages; i++) {
          backendGetRequest("api/generic-tag/", { params: { page_index: i, group_type: BA_TAG } })
            .then((res) => {
              const generic_tags = res?.data?.generic_tags;
              setFlattenedTags(prevState => [...prevState, ...generic_tags]);
              setFlattenedTagsPageCountReceived(prevState => prevState + 1);
            })
            .catch(() => {
              setFlattenedTagsPageCountReceived(prevState => prevState - 1);
            })
        }
      });
  }, []);

  return (
    <OuterBox>
      <HeaderBox
        title='Placement Settings'
        actions={
          <PermissionGate
            requiredRole='dashboardEditor'
            readOnly={
              <Button
                size='small'
                variant='contained'
                sx={{ marginLeft: 2, fontSize: 14, textTransform: 'none' }}
                disabled
                type='none'
              >
                + Add New Purpose
              </Button>
            }
            toolTipText='Only admins and dashboard editors may add new bank account purposes'
            toolTipPlacement='left'
          >
            <GenericModal
              modalTitle='Add Bank Account Purpose'
              visible={addPurposeVisible}
              showModal={() => setAddPurposeVisible(true)}
              handleCancelModal={() => {
                setAddPurposeVisible(false)
                form.resetFields()
              }}
              modalBody={
                <Form form={form} layout='vertical'>
                  <Form.Item
                    name='purposeName'
                    label='Purpose Name'
                    rules={[{ required: true, message: 'Please enter a purpose name' }]}
                  >
                    <Input placeholder='Enter purpose name' />
                  </Form.Item>
                  <Form.Item name='purposeDescription' label='Purpose Description'>
                    <Input.TextArea placeholder='Enter purpose description' rows={4} />
                  </Form.Item>
                  <Box display='flex' justifyContent='end' mt={2}>
                    <Button
                      onClick={() => setAddPurposeVisible(false)}
                      disableElevation
                      size='large'
                      type='submit'
                      variant='outlined'
                      color='secondary'
                      sx={{ ml: 2, fontSize: 14, textTransform: 'none' }}
                    >
                      Cancel
                    </Button>
                    <LoadingButton
                      onClick={addPurpose}
                      loading={addPurposeSubmitting}
                      disableElevation
                      size='large'
                      type='submit'
                      variant='contained'
                      color='primary'
                      sx={{ marginLeft: 2, fontSize: 14, textTransform: 'none' }}
                    >
                      Add Purpose
                    </LoadingButton>
                  </Box>
                </Form>
              }
              size='md'
              button={
                <Button
                  disableElevation
                  size='small'
                  type='submit'
                  variant='contained'
                  color='primary'
                  sx={{ marginLeft: 2, fontSize: 14, textTransform: 'none' }}
                >
                  + Add New Purpose
                </Button>
              }
            />
          </PermissionGate>
        }
      />
      <Purposes purposeArr={savedPurposes} onSuccess={setSavedPurposes} />
      <CustomTagsHierarchy
        title={'Hierarchies'}
        group_type={BA_TAG}
        flattenedTagsLoading={flattenedTagsLoading}
        flattenedTags={flattenedTags}
        groupsLoading={groupsLoading}
        groups={groups}
        addCustomTag={addCustomTag}
        deleteCustomTag={deleteCustomTag}
        editCustomTag={editCustomTag}
        addGroup={addGroup}
        deleteGroup={deleteGroup}
        editGroup={editGroup}
      />
    </OuterBox>
  )
};

export default PlacementSettings;
